<template>
    <v-app class="bookmark-app">
        <v-layout>
            <CoreToast ref="CoreToast"/>
            <v-app-bar
                v-if="!isClearSkyRoute"
                height="64"
                :class="['bookmark-app-bar', { 'bookmark-app-bar--compact': isCompactNav }]"
            >
                <v-app-bar-title
                    :class="['bookmark-app-title', { 'bookmark-app-title--link': !isSiteAccessRoute }]"
                    :aria-label="isSiteAccessRoute ? undefined : 'Go to AnimeBookmark home'"
                    :role="isSiteAccessRoute ? undefined : 'link'"
                    :tabindex="isSiteAccessRoute ? undefined : 0"
                    @click="goHomeFromTitle"
                    @keydown.enter="goHomeFromTitle"
                    @keydown.space.prevent="goHomeFromTitle"
                >
                    <img
                        :src="toolbarLogoSource"
                        class="bookmark-app-logo"
                        width="190"
                        height="47"
                        alt="AnimeBookmark"
                    >
                </v-app-bar-title>
                <nav v-if="!isCompactNav && !isFocusedPublicRoute" class="bookmark-primary-nav" aria-label="Primary navigation">
                    <v-btn
                        to="/"
                        :class="['bookmark-buttons', { 'bookmark-buttons--active': isPrimaryTabActive('home') }]"
                        :aria-current="isPrimaryTabActive('home') ? 'page' : undefined"
                        :ripple="false"
                        :prepend-icon="mdiHomeOutline"
                        stacked
                        @click="rememberPrimaryTab('home')"
                    >
                        Home
                    </v-btn>
                    <v-btn
                        :to="currentSeasonPath"
                        :class="['bookmark-buttons', { 'bookmark-buttons--active': isPrimaryTabActive('seasons') }]"
                        :aria-current="isPrimaryTabActive('seasons') ? 'page' : undefined"
                        :ripple="false"
                        :prepend-icon="mdiViewGridOutline"
                        stacked
                        @click="rememberPrimaryTab('seasons')"
                    >
                        Seasons
                    </v-btn>
                    <v-btn
                        to="/releases/"
                        :class="['bookmark-buttons', { 'bookmark-buttons--active': isPrimaryTabActive('releases') }]"
                        :aria-current="isPrimaryTabActive('releases') ? 'page' : undefined"
                        :ripple="false"
                        :prepend-icon="mdiCalendarRange"
                        stacked
                        @click="rememberPrimaryTab('releases')"
                    >
                        Releases
                    </v-btn>
                    <v-btn
                        v-if="newsEnabled"
                        to="/news/"
                        :class="['bookmark-buttons', { 'bookmark-buttons--active': isPrimaryTabActive('news') }]"
                        :aria-current="isPrimaryTabActive('news') ? 'page' : undefined"
                        :ripple="false"
                        :prepend-icon="mdiNewspaperVariantOutline"
                        stacked
                        @click="rememberPrimaryTab('news')"
                    >
                        News
                    </v-btn>
                    <v-btn
                        to="/discover/"
                        :class="['bookmark-buttons', { 'bookmark-buttons--active': isPrimaryTabActive('discover') }]"
                        :aria-current="isPrimaryTabActive('discover') ? 'page' : undefined"
                        :ripple="false"
                        :prepend-icon="mdiCompassOutline"
                        stacked
                        @click="rememberPrimaryTab('discover')"
                    >
                        Discover
                    </v-btn>
                    <v-btn
                        v-if="isLoggedIn"
                        to="/bookmarks/"
                        :class="['bookmark-buttons', { 'bookmark-buttons--active': isPrimaryTabActive('bookmarks') }]"
                        :aria-current="isPrimaryTabActive('bookmarks') ? 'page' : undefined"
                        :ripple="false"
                        :prepend-icon="mdiBookmarkOutline"
                        stacked
                        @click="rememberPrimaryTab('bookmarks')"
                    >
                        Bookmarks
                    </v-btn>
                    <v-btn
                        to="/archive/"
                        :class="['bookmark-buttons', { 'bookmark-buttons--active': isPrimaryTabActive('archive') }]"
                        :aria-current="isPrimaryTabActive('archive') ? 'page' : undefined"
                        :ripple="false"
                        :prepend-icon="mdiArchiveOutline"
                        stacked
                        @click="rememberPrimaryTab('archive')"
                    >
                        Archive
                    </v-btn>
                    <v-btn
                        v-if="showWatchOrderNav"
                        to="/watch-order/"
                        :class="['bookmark-buttons', { 'bookmark-buttons--active': isPrimaryTabActive('watch-order') }]"
                        :aria-current="isPrimaryTabActive('watch-order') ? 'page' : undefined"
                        :ripple="false"
                        :prepend-icon="mdiPlayCircleOutline"
                        stacked
                        @click="rememberPrimaryTab('watch-order')"
                    >
                        Watch Order
                    </v-btn>
                </nav>
                <div v-if="!isCompactNav && !isFocusedPublicRoute" class="bookmark-avatar-wrapper">
                    <CoreGoogleLogin mode="bar" :stay-on-current-route-on-login="$route.name === 'BillingView'" />
                </div>
                <template v-else-if="!isFocusedPublicRoute">
                    <v-btn
                        icon
                        variant="text"
                        class="bookmark-mobile-menu-btn"
                        :aria-label="mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'"
                        @click="mobileNavOpen = !mobileNavOpen"
                    >
                        <v-icon :icon="mdiMenu" />
                    </v-btn>
                </template>
            </v-app-bar>
            <v-navigation-drawer
                v-if="!isFocusedPublicRoute"
                v-model="mobileNavOpen"
                location="right"
                temporary
                width="290"
                class="bookmark-mobile-drawer"
            >
                <div class="bookmark-mobile-drawer-header">
                    <div class="bookmark-mobile-drawer-title">Menu</div>
                    <v-btn
                        icon
                        variant="text"
                        class="bookmark-mobile-drawer-close"
                        aria-label="Close navigation menu"
                        @click="mobileNavOpen = false"
                    >
                        <v-icon :icon="mdiClose" />
                    </v-btn>
                </div>
                <v-divider></v-divider>
                <nav class="bookmark-mobile-nav" aria-label="Primary navigation">
                    <v-btn block variant="text" :class="['bookmark-mobile-nav-btn', { 'bookmark-mobile-nav-btn--active': isPrimaryTabActive('home') }]" :prepend-icon="mdiHomeOutline" @click="goToAndClose('/')">
                        Home
                    </v-btn>
                    <v-btn block variant="text" :class="['bookmark-mobile-nav-btn', { 'bookmark-mobile-nav-btn--active': isPrimaryTabActive('seasons') }]" :prepend-icon="mdiViewGridOutline" @click="goToAndClose(currentSeasonPath)">
                        Seasons
                    </v-btn>
                    <v-btn block variant="text" :class="['bookmark-mobile-nav-btn', { 'bookmark-mobile-nav-btn--active': isPrimaryTabActive('releases') }]" :prepend-icon="mdiCalendarRange" @click="goToAndClose('/releases/')">
                        Releases
                    </v-btn>
                    <v-btn v-if="newsEnabled" block variant="text" :class="['bookmark-mobile-nav-btn', { 'bookmark-mobile-nav-btn--active': isPrimaryTabActive('news') }]" :prepend-icon="mdiNewspaperVariantOutline" @click="goToAndClose('/news/')">
                        News
                    </v-btn>
                    <v-btn block variant="text" :class="['bookmark-mobile-nav-btn', { 'bookmark-mobile-nav-btn--active': isPrimaryTabActive('discover') }]" :prepend-icon="mdiCompassOutline" @click="goToAndClose('/discover/')">
                        Discover
                    </v-btn>
                    <v-btn
                        v-if="isLoggedIn"
                        block
                        variant="text"
                        :class="['bookmark-mobile-nav-btn', { 'bookmark-mobile-nav-btn--active': isPrimaryTabActive('bookmarks') }]"
                        :prepend-icon="mdiBookmarkOutline"
                        @click="goToAndClose('/bookmarks/')"
                    >
                        Bookmarks
                    </v-btn>
                    <v-btn block variant="text" :class="['bookmark-mobile-nav-btn', { 'bookmark-mobile-nav-btn--active': isPrimaryTabActive('archive') }]" :prepend-icon="mdiArchiveOutline" @click="goToAndClose('/archive/')">
                        Archive
                    </v-btn>
                    <v-btn v-if="showWatchOrderNav" block variant="text" :class="['bookmark-mobile-nav-btn', { 'bookmark-mobile-nav-btn--active': isPrimaryTabActive('watch-order') }]" :prepend-icon="mdiPlayCircleOutline" @click="goToAndClose('/watch-order/')">
                        Watch Order
                    </v-btn>
                </nav>
                <v-divider></v-divider>
                <div class="bookmark-mobile-actions">
                    <div class="bookmark-mobile-login">
                        <CoreGoogleLogin
                            v-if="mobileNavOpen || isLoggedIn"
                            mode="drawer"
                            :stay-on-current-route-on-login="$route.name === 'BillingView'"
                        />
                    </div>
                </div>
            </v-navigation-drawer>
            <router-view :key="$route.name === 'AnimeListView' ? 'seasons' : $route.path" name="AnimeListView"></router-view>
            <router-view name="AnimeDetailsView"></router-view>
            <router-view name="ReleasesView"></router-view>
            <router-view name="AnimeBookmarksView"></router-view>
            <router-view name="WatchingView"></router-view>
        </v-layout>
        <v-dialog
            :model-value="signupFunnelPromptOpen"
            max-width="480"
            :scrim="funnelDialogScrim"
            @update:model-value="handleSignupFunnelDialogToggle"
        >
            <v-card class="signup-funnel-dialog__card" data-signup-funnel-ignore>
                <button
                    type="button"
                    class="signup-funnel-dialog__close"
                    aria-label="Keep browsing"
                    @click="dismissSignupFunnelPrompt('close_button')"
                >
                    <v-icon :icon="mdiClose" size="18" />
                </button>
                <div class="signup-funnel-dialog__brand">
                    <img src="/pwa-icon-192x192.png" alt="" width="38" height="38">
                    <span>AnimeBookmark</span>
                </div>
                <div class="signup-funnel-dialog__visual" aria-hidden="true">
                    <div class="signup-funnel-dialog__icon">
                        <svg viewBox="0 0 32 40" fill="none"><path d="M7 5h18v30l-9-6-9 6V5Z" /><path d="M12 12h8M12 18h8" /></svg>
                    </div>
                </div>
                <div class="signup-funnel-dialog__content">
                    <v-card-title class="signup-funnel-dialog__title">
                        Enjoying AnimeBookmark?
                    </v-card-title>
                    <v-card-text class="signup-funnel-dialog__body">
                        <p>
                            Create a free account to save your anime, track episodes, and keep your watchlist synced for later.
                        </p>
                    </v-card-text>
                </div>
                <v-card-actions class="signup-funnel-dialog__actions">
                    <v-btn
                        variant="outlined"
                        class="signup-funnel-dialog__secondary"
                        @click="dismissSignupFunnelPrompt('maybe_later')"
                    >
                        Maybe later
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        :prepend-icon="mdiAccountPlusOutline"
                        class="signup-funnel-dialog__primary"
                        @click="acceptSignupFunnelPrompt"
                    >
                        Create free account
                    </v-btn>
                </v-card-actions>
                <button
                    type="button"
                    class="signup-funnel-dialog__link"
                    @click="dismissSignupFunnelPrompt('keep_browsing')"
                >
                    Keep browsing
                </button>
            </v-card>
        </v-dialog>
        <CoreBookmarkLoginDialog />
    </v-app>
</template>

<script>
    // icons https://pictogrammers.com/library/mdi/
    const PRIMARY_TAB_STORAGE_KEY = 'bookmark-last-primary-tab';
    const SHOW_WATCH_ORDER_NAV = true;
    const SIGNUP_FUNNEL_IGNORE_SELECTOR = [
        '[data-signup-funnel-ignore]',
        '.auth-dialog__card',
        '.bookmark-avatar-wrapper',
        '.bookmark-mobile-actions',
        '.bookmark-mobile-menu-btn',
        '.bookmark-mobile-drawer-close',
        '.video-card'
    ].join(',');
    const SIGNUP_FUNNEL_INTERACTION_SELECTOR = [
        '.bookmark-buttons',
        '.bookmark-mobile-nav-btn',
        '.bookmark-season-nav .v-tab',
        '[role="tab"]',
        '.anime-summary-card',
        '.title-overlay-link',
        '.overview-bookmark-btn',
        '.overview-watching-link',
        '.bookmark-icon-btn',
        '.card-action-btn',
        'a[href]',
        'button'
    ].join(',');

    import { 
        mdiArchiveOutline, mdiBookmarkOutline,
        mdiHomeOutline, mdiCalendarRange, mdiCompassOutline, mdiMenu, mdiClose, mdiViewGridOutline,
        mdiPlayCircleOutline, mdiAccountPlusOutline,
        mdiNewspaperVariantOutline
    } from '@mdi/js';
    import { computed, watch } from 'vue';
    import { useDisplay, useTheme } from 'vuetify';
    import toolbarLogoDark from './assets/branding/editorial-ligature/animebookmark-editorial-logo-dark.png';
    import toolbarLogoLight from './assets/branding/editorial-ligature/animebookmark-editorial-logo-light.png';
    import CoreBookmarkLoginDialog from './core/components/CoreBookmarkLoginDialog.vue';
    import CoreGoogleLogin from './core/components/CoreGoogleLogin.vue';
    import CoreToast from './core/components/CoreToast.vue';
    import './core/styles/siteToolbar.css';
    import { trackSiteEvent } from './core/services/analytics';
    import { useAuthPrompt } from './core/store/authPrompt.store';
    import { useAnnouncements } from './core/store/announcement.store';
    import { getCurrentSeasonPath, usePreferences } from './core/store/preferences.store';
    import { useSignupFunnel } from './core/store/signupFunnel.store';
    import { useSiteFeatures } from './core/store/siteFeatures.store';
    import { storeToRefs } from 'pinia';
    import { useNotification } from './core/store/notification.store';
    import { useUser } from './core/store/user.store';

    export default {
        name: 'App',
        components: {
            CoreBookmarkLoginDialog, CoreGoogleLogin, CoreToast
        },
        data: function() {
            return {
                mdiArchiveOutline: mdiArchiveOutline,
                mdiBookmarkOutline: mdiBookmarkOutline,
                mdiHomeOutline: mdiHomeOutline,
                mdiViewGridOutline: mdiViewGridOutline,
                mdiCalendarRange: mdiCalendarRange,
                mdiNewspaperVariantOutline: mdiNewspaperVariantOutline,
                mdiCompassOutline: mdiCompassOutline,
                mdiPlayCircleOutline: mdiPlayCircleOutline,
                mdiMenu: mdiMenu,
                mdiClose: mdiClose,
                mdiAccountPlusOutline: mdiAccountPlusOutline,
                mobileNavOpen: false,
                activePrimaryTab: '',
                announcementRefreshTimer: null
            }
        },
        methods: {
            trackSignupFunnelEvent(eventName = '', properties = {}) {
                if (!eventName || typeof window === 'undefined') {
                    return;
                }
                trackSiteEvent({
                    eventName,
                    pagePath: this.$route?.path || '/',
                    source: 'signup_funnel',
                    properties: {
                        fullPath: this.$route?.fullPath || '',
                        routeName: `${this.$route?.name || ''}`,
                        ...properties
                    },
                    skipReddit: true
                });
            },
            syncSignupFunnelForRoute(route = this.$route) {
                const result = this.signupFunnelState.hydrateFromRoute(route, {
                    isLoggedIn: this.isLoggedIn,
                    isPromptSuppressedRoute: (
                        ['SiteAccessView', 'PrivacyView', 'TermsView', 'RefundPolicyView', 'ExtensionSignupView'].includes(route?.name)
                        || ['PricingView', 'BillingView'].includes(route?.name)
                        || ['TrackerView', 'ContactView'].includes(route?.name)
                        || route?.name === 'TrackerActivityView'
                    )
                });

                if (result.shouldTrackLanding) {
                    this.signupFunnelState.markLandingTracked();
                    this.trackSignupFunnelEvent('signup_funnel_landing', {
                        trigger: 'query'
                    });
                }
            },
            shouldIgnoreSignupFunnelTarget(target = null) {
                return !target || typeof target.closest !== 'function' || Boolean(target.closest(SIGNUP_FUNNEL_IGNORE_SELECTOR));
            },
            isSignupFunnelInteractionTarget(target = null) {
                if (this.shouldIgnoreSignupFunnelTarget(target)) {
                    return false;
                }
                if (!target.closest('.bookmark-app')) {
                    return false;
                }
                return Boolean(target.closest(SIGNUP_FUNNEL_INTERACTION_SELECTOR));
            },
            handleSignupFunnelInteraction(event) {
                if (
                    !this.signupFunnelState?.canPrompt
                    || this.isLoggedIn
                    || this.authDialogOpen
                    || this.isSiteAccessRoute
                    || ['PrivacyView', 'TermsView', 'RefundPolicyView', 'ExtensionSignupView'].includes(this.$route?.name)
                    || ['PricingView', 'BillingView'].includes(this.$route?.name)
                    || ['TrackerView', 'ContactView'].includes(this.$route?.name)
                ) {
                    return;
                }
                const target = event?.target || null;
                if (!this.isSignupFunnelInteractionTarget(target)) {
                    return;
                }
                this.signupFunnelState.requestPrompt('site_interaction');
            },
            handleSignupFunnelKeydown(event) {
                if (!['Enter', ' '].includes(event?.key)) {
                    return;
                }
                this.handleSignupFunnelInteraction(event);
            },
            handleSignupFunnelDialogToggle(value) {
                if (value) {
                    return;
                }
                this.dismissSignupFunnelPrompt('dialog_close');
            },
            acceptSignupFunnelPrompt() {
                this.trackSignupFunnelEvent('signup_funnel_accept', {
                    reason: this.signupFunnelState.lastReason || 'interaction'
                });
                this.signupFunnelState.acceptPrompt();
                this.authPromptState.openRegisterDialog({
                    context: 'general',
                    stayOnCurrentRouteOnLogin: true
                });
            },
            dismissSignupFunnelPrompt(reason = 'dismiss') {
                if (!this.signupFunnelState.promptOpen && this.signupFunnelState.dismissed) {
                    return;
                }
                this.trackSignupFunnelEvent('signup_funnel_dismiss', {
                    reason
                });
                this.signupFunnelState.dismissPrompt();
            },
            addSignupFunnelInteractionListeners() {
                if (typeof document === 'undefined') {
                    return;
                }
                document.addEventListener('click', this.handleSignupFunnelInteraction, true);
                document.addEventListener('keydown', this.handleSignupFunnelKeydown, true);
            },
            removeSignupFunnelInteractionListeners() {
                if (typeof document === 'undefined') {
                    return;
                }
                document.removeEventListener('click', this.handleSignupFunnelInteraction, true);
                document.removeEventListener('keydown', this.handleSignupFunnelKeydown, true);
            },
            refreshUnreadAnnouncementCount() {
                if (!this.isLoggedIn) {
                    return;
                }
                this.announcementState.loadUnreadCount({ force: true }).catch(() => {
                    // The badge is non-blocking. A later poll or focus event retries it.
                });
            },
            handleAnnouncementWindowFocus() {
                this.refreshUnreadAnnouncementCount();
            },
            startAnnouncementMonitoring() {
                this.stopAnnouncementMonitoring({ reset: false });
                if (!this.isLoggedIn || typeof window === 'undefined') {
                    return;
                }
                this.refreshUnreadAnnouncementCount();
                window.addEventListener('focus', this.handleAnnouncementWindowFocus);
                this.announcementRefreshTimer = window.setInterval(
                    this.refreshUnreadAnnouncementCount,
                    60000
                );
            },
            stopAnnouncementMonitoring({ reset = true } = {}) {
                if (typeof window !== 'undefined') {
                    window.removeEventListener('focus', this.handleAnnouncementWindowFocus);
                    if (this.announcementRefreshTimer) {
                        window.clearInterval(this.announcementRefreshTimer);
                    }
                }
                this.announcementRefreshTimer = null;
                if (reset) {
                    this.announcementState.resetUnreadCount();
                }
            },
            readStoredPrimaryTab() {
                if (typeof window === 'undefined' || !window.sessionStorage) {
                    return '';
                }
                return `${window.sessionStorage.getItem(PRIMARY_TAB_STORAGE_KEY) || ''}`.trim().toLowerCase();
            },
            setActivePrimaryTab(tab = '') {
                const normalizedTab = `${tab || ''}`.trim().toLowerCase();
                this.activePrimaryTab = normalizedTab;
                if (typeof window === 'undefined' || !window.sessionStorage) {
                    return;
                }
                if (normalizedTab) {
                    window.sessionStorage.setItem(PRIMARY_TAB_STORAGE_KEY, normalizedTab);
                    return;
                }
                window.sessionStorage.removeItem(PRIMARY_TAB_STORAGE_KEY);
            },
            rememberPrimaryTab(tab = '') {
                this.setActivePrimaryTab(tab);
            },
            getPrimaryTabForRoute(route = this.$route) {
                const routeName = `${route?.name || ''}`;
                if (
                    ['SharedBookmarksView', 'PrivacyView', 'TermsView', 'RefundPolicyView', 'ExtensionSignupView'].includes(routeName)
                    || ['PricingView', 'BillingView'].includes(routeName)
                    || ['TrackerView', 'ContactView'].includes(routeName)
                ) {
                    return '__clear__';
                }
                if (routeName === 'HomeView') {
                    return 'home';
                }
                if (routeName === 'AnimeListView') {
                    return 'seasons';
                }
                if (routeName === 'ReleasesView') {
                    return 'releases';
                }
                if (routeName === 'NewsView' || routeName === 'NewsArticleView') {
                    return 'news';
                }
                if (['WatchOrderSelectorView', 'WatchOrderHubView'].includes(routeName)) {
                    return 'watch-order';
                }
                if (routeName === 'DiscoverView') {
                    return 'discover';
                }
                if (routeName === 'AnimeSearchView') {
                    return 'discover';
                }
                if (['AnimeBookmarksView', 'TrackerActivityView'].includes(routeName)) {
                    return 'bookmarks';
                }
                if (routeName === 'ArchiveView' || routeName === 'ArchiveSeasonView') {
                    return 'archive';
                }
                return '';
            },
            syncPrimaryTabForRoute(route = this.$route) {
                if (!route || route.name === 'SiteAccessView') {
                    this.setActivePrimaryTab('');
                    return;
                }

                const mappedTab = this.getPrimaryTabForRoute(route);
                if (mappedTab === '__clear__') {
                    this.setActivePrimaryTab('');
                    return;
                }
                if (mappedTab) {
                    this.setActivePrimaryTab(mappedTab);
                    return;
                }

                if (!this.activePrimaryTab) {
                    const storedTab = this.readStoredPrimaryTab();
                    if (storedTab) {
                        this.activePrimaryTab = storedTab;
                    }
                }
            },
            isPrimaryTabActive(tab = '') {
                return this.activePrimaryTab === `${tab || ''}`.trim().toLowerCase();
            },
            goHomeFromTitle() {
                if (this.isSiteAccessRoute || this.$route.path === '/') {
                    return;
                }
                this.rememberPrimaryTab('home');
                this.$router.push('/');
            },
            goToAndClose(path) {
                this.mobileNavOpen = false;
                const mappedTab = this.getPrimaryTabForRoute({ name: this.getPrimaryTabNameFromPath(path) });
                if (mappedTab) {
                    this.rememberPrimaryTab(mappedTab);
                }
                this.$router.push(path);
            },
            getPrimaryTabNameFromPath(path = '') {
                const normalizedPath = `${path || ''}`;
                if (normalizedPath === '/') {
                    return 'HomeView';
                }
                if (normalizedPath.startsWith('/seasons')) {
                    return 'AnimeListView';
                }
                if (normalizedPath.startsWith('/releases')) {
                    return 'ReleasesView';
                }
                if (normalizedPath.startsWith('/news')) {
                    return 'NewsView';
                }
                if (normalizedPath.startsWith('/watch-order')) {
                    return 'WatchOrderHubView';
                }
                if (normalizedPath.startsWith('/discover')) {
                    return 'DiscoverView';
                }
                if (normalizedPath.startsWith('/search')) {
                    return 'AnimeSearchView';
                }
                if (normalizedPath.startsWith('/bookmarks')) {
                    return 'AnimeBookmarksView';
                }
                if (normalizedPath.startsWith('/archive')) {
                    return 'ArchiveView';
                }
                return '';
            },
        },
        watch: {
            message(newValue) {
                this.$root.CoreToast.show(newValue);
            },
            isCompactNav(newValue) {
                if (!newValue) {
                    this.mobileNavOpen = false;
                }
            },
            $route(to) {
                this.syncPrimaryTabForRoute(to);
                this.syncSignupFunnelForRoute(to);
            },
            isLoggedIn(newValue) {
                this.syncSignupFunnelForRoute(this.$route);
                if (newValue) {
                    this.startAnnouncementMonitoring();
                    return;
                }
                this.stopAnnouncementMonitoring();
            },
            signupFunnelPromptOpen(newValue) {
                if (newValue) {
                    this.trackSignupFunnelEvent('signup_funnel_prompt_view', {
                        reason: this.signupFunnelState.lastReason || 'interaction'
                    });
                }
            }
        },
        computed: {
            currentSeasonPath() {
                return getCurrentSeasonPath();
            },
            isSiteAccessRoute() {
                return this.$route.name === 'SiteAccessView';
            },
            isFocusedPublicRoute() {
                return this.isClearSkyRoute || ['SiteAccessView', 'ExtensionSignupView'].includes(this.$route.name);
            },
            isClearSkyRoute() {
                return ['TrackerView', 'PricingView', 'BillingView', 'TermsView', 'RefundPolicyView', 'PrivacyView', 'ContactView'].includes(this.$route.name);
            },
            signupFunnelPromptOpen() {
                return this.signupFunnelState.promptOpen;
            },
            funnelDialogScrim() {
                return this.themePreference === 'light'
                    ? 'rgba(5, 8, 14, 0.28)'
                    : 'rgba(6, 10, 18, 0.74)';
            },
            toolbarLogoSource() {
                return this.themePreference === 'light'
                    ? toolbarLogoLight
                    : toolbarLogoDark;
            }
        },
        mounted() {
            this.$root.CoreToast = this.$refs.CoreToast;
            this.syncPrimaryTabForRoute(this.$route);
            this.syncSignupFunnelForRoute(this.$route);
            this.addSignupFunnelInteractionListeners();
            this.startAnnouncementMonitoring();
        },
        beforeUnmount() {
            this.removeSignupFunnelInteractionListeners();
            this.stopAnnouncementMonitoring({ reset: false });
        },
        setup() {
            const notificationState = useNotification();
            const { message } = storeToRefs(notificationState);
            const userState = useUser();
            const { isLoggedIn } = storeToRefs(userState);
            const siteFeaturesState = useSiteFeatures();
            const { newsEnabled } = storeToRefs(siteFeaturesState);
            const preferencesState = usePreferences();
            const { theme: themePreference } = storeToRefs(preferencesState);
            const signupFunnelState = useSignupFunnel();
            const authPromptState = useAuthPrompt();
            const announcementState = useAnnouncements();
            const { dialogOpen: authDialogOpen } = storeToRefs(authPromptState);
            const theme = useTheme();
            const display = useDisplay();
            const watchOrderNavWidth = SHOW_WATCH_ORDER_NAV ? 110 : 0;
            const newsNavWidth = computed(() => newsEnabled.value ? 78 : 0);
            const toolbarLogoWidthIncrease = 50;
            const navCollapseWidth = computed(() => (
                isLoggedIn.value
                    ? 1260 + newsNavWidth.value - watchOrderNavWidth + toolbarLogoWidthIncrease
                    : 1160 + newsNavWidth.value - watchOrderNavWidth + toolbarLogoWidthIncrease
            ));
            const isCompactNav = computed(() => display.width.value <= navCollapseWidth.value);

            preferencesState.hydrateFromStorage();
            siteFeaturesState.ensureLoaded();

            watch(themePreference, (nextTheme) => {
                theme.global.name.value = nextTheme;
            }, {
                immediate: true
            });

            return {
                message,
                isLoggedIn,
                newsEnabled,
                isCompactNav,
                showWatchOrderNav: SHOW_WATCH_ORDER_NAV,
                themePreference,
                signupFunnelState,
                authPromptState,
                announcementState,
                authDialogOpen
            };
        }
    }
</script>

<style>
    body {
        background-color: rgb(var(--v-theme-background));
        color: rgb(var(--v-theme-on-background));
        transition: background-color 0.2s ease;
    }
    .signup-funnel-dialog__card {
        --signup-funnel-paper: #1a212b;
        --signup-funnel-soft: #222f40;
        --signup-funnel-ink: #eff5fc;
        --signup-funnel-muted: #adbed0;
        --signup-funnel-line: #3d4e61;
        --signup-funnel-accent: #8bc0ff;
        --signup-funnel-on-accent: #112235;
        --signup-funnel-accent-soft: #243c58;
        --signup-funnel-shadow: #00000050;
        position: relative;
        overflow: hidden !important;
        border-radius: 25px !important;
        padding: 29px 39px 0 !important;
        color: var(--signup-funnel-ink) !important;
        background: var(--signup-funnel-paper);
        border: 1px solid var(--signup-funnel-line);
        box-shadow: 0 20px 50px var(--signup-funnel-shadow) !important;
        font-family: 'Segoe UI', Roboto, Arial, sans-serif;
    }
    .signup-funnel-dialog__brand {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding-right: 4px;
        font-size: 16px;
        font-weight: 650;
        letter-spacing: -0.4px;
    }
    .signup-funnel-dialog__brand img {
        flex-shrink: 0;
        border-radius: 9px;
    }
    .signup-funnel-dialog__card .signup-funnel-dialog__close {
        position: absolute;
        top: 15px;
        right: 15px;
        z-index: 2;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border: 0;
        border-radius: 999px;
        color: var(--signup-funnel-muted);
        background: transparent;
        cursor: pointer;
        transition: background 0.18s ease, color 0.18s ease;
    }
    .signup-funnel-dialog__close:hover,
    .signup-funnel-dialog__close:focus-visible {
        color: var(--signup-funnel-ink);
        background: var(--signup-funnel-soft);
    }
    .signup-funnel-dialog__card :is(button, .v-btn):focus-visible {
        outline: 2px solid var(--signup-funnel-accent);
        outline-offset: 3px;
    }
    .signup-funnel-dialog__visual {
        display: flex;
        justify-content: center;
        margin: 29px 0 19px;
    }
    .signup-funnel-dialog__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 61px;
        height: 65px;
        border-radius: 20px;
        color: var(--signup-funnel-accent);
        background: var(--signup-funnel-accent-soft);
    }
    .signup-funnel-dialog__icon svg {
        width: 30px;
        height: 38px;
        stroke: currentColor;
        stroke-width: 1.7;
        stroke-linecap: round;
        stroke-linejoin: round;
    }
    .signup-funnel-dialog__card .v-card-title.signup-funnel-dialog__title {
        color: var(--signup-funnel-ink);
        font-size: clamp(1.5rem, 3vw, 1.8125rem);
        font-weight: 700;
        letter-spacing: -0.9px;
        line-height: 1.15;
        padding: 0 !important;
        text-align: center;
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
    }
    .signup-funnel-dialog__card .v-card-text.signup-funnel-dialog__body {
        color: var(--signup-funnel-muted);
        font-size: 14px;
        font-weight: 400;
        line-height: 1.65;
        max-width: 46ch;
        margin: 0 auto;
        padding: 10px 0 0 !important;
        text-align: center;
    }
    .signup-funnel-dialog__body p {
        margin: 0;
    }
    .signup-funnel-dialog__actions {
        display: grid !important;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr);
        gap: 8px;
        padding: 25px 0 23px !important;
    }
    .signup-funnel-dialog__actions .v-btn {
        width: 100%;
        margin-inline: 0 !important;
    }
    .signup-funnel-dialog__secondary,
    .signup-funnel-dialog__primary {
        min-height: 46px;
        padding-inline: 12px !important;
        border-radius: 10px !important;
        text-transform: none !important;
        letter-spacing: 0 !important;
        font-size: 13px !important;
        font-weight: 650 !important;
    }
    .signup-funnel-dialog__secondary {
        color: var(--signup-funnel-ink) !important;
        border-color: var(--signup-funnel-line) !important;
        background: var(--signup-funnel-paper) !important;
    }
    .signup-funnel-dialog__card .signup-funnel-dialog__primary {
        color: var(--signup-funnel-on-accent) !important;
        background: var(--signup-funnel-accent) !important;
        box-shadow: none !important;
    }
    .signup-funnel-dialog__primary > .v-btn__overlay,
    .signup-funnel-dialog__secondary > .v-btn__overlay {
        transition: opacity 0.18s ease;
    }
    .signup-funnel-dialog__card .signup-funnel-dialog__link {
        display: block;
        width: calc(100% + 78px);
        margin: 0 -39px;
        border: 0;
        border-top: 1px solid var(--signup-funnel-line);
        padding: 17px 14px;
        color: var(--signup-funnel-muted);
        background: var(--signup-funnel-soft);
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
    }
    .signup-funnel-dialog__link:hover,
    .signup-funnel-dialog__link:focus-visible {
        color: var(--signup-funnel-accent);
    }
    .signup-funnel-dialog__link:focus-visible {
        outline-offset: -4px !important;
    }
    .v-theme--light .signup-funnel-dialog__card {
        --signup-funnel-paper: #fff;
        --signup-funnel-soft: #f0f5fb;
        --signup-funnel-ink: #172b40;
        --signup-funnel-muted: #5d7085;
        --signup-funnel-line: #d4dfeb;
        --signup-funnel-accent: #2862aa;
        --signup-funnel-on-accent: #fff;
        --signup-funnel-accent-soft: #e7f0fc;
        --signup-funnel-shadow: #183a5d18;
    }
    .v-dialog .v-overlay__scrim {
        background: rgb(2, 4, 8) !important;
        background-color: rgb(2, 4, 8) !important;
        opacity: 0.86 !important;
    }
    .v-overlay.v-theme--light .v-overlay__scrim,
    .v-theme--light .v-overlay__scrim {
        background: rgb(5, 8, 14) !important;
        background-color: rgb(5, 8, 14) !important;
        opacity: 0.24 !important;
    }
    .v-overlay.v-theme--light.v-dialog .v-overlay__scrim,
    .v-theme--light .v-dialog .v-overlay__scrim {
        opacity: 0.24 !important;
    }
    .ab-dark-overlay-dialog.v-dialog .v-overlay__scrim {
        opacity: 0.92 !important;
    }
    .v-overlay.v-theme--light.ab-dark-overlay-dialog .v-overlay__scrim,
    .v-theme--light .ab-dark-overlay-dialog.v-dialog .v-overlay__scrim {
        opacity: 0.36 !important;
    }
    @media (max-width: 600px) {
        .signup-funnel-dialog__card {
            padding: 27px 23px 0 !important;
        }
        .signup-funnel-dialog__brand {
            font-size: 15px;
        }
        .signup-funnel-dialog__actions {
            grid-template-columns: 1fr;
        }
        .signup-funnel-dialog__primary {
            order: -1;
        }
        .signup-funnel-dialog__card .signup-funnel-dialog__link {
            width: calc(100% + 46px);
            margin-inline: -23px;
        }
    }
</style>
